import { Injectable } from '@angular/core';
import { State, Action, Selector, StateContext } from '@ngxs/store';
import { catchError, map } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { NotificationService } from '../../../../../common/services/notification/notification.service';
import { TransportPlannerProfileItemBriefService } from './transport-planner-profile-item-brief.service';
import { TransportPlannerProfileDetails, TransportPlannerProfileModel } from '../models';
import { NavigateUserService } from '../../../../services/navigate-user.service';
import {
  TransportPlannerProfileItemBriefClone,
  TransportPlannerProfileItemBriefDelete,
  TransportPlannerProfileItemBriefInitialState,
  TransportPlannerProfileItemBriefLoad,
  TransportPlannerProfileItemBriefSave,
} from './transport-planner-profile-item-brief.actions';
import { TransportPlannerProfileItemService } from '../item/transport-planner-profile-item.service';
import { CustomHttpErrorResponse, Error } from '../../../../../common';
import { MatSnackBarRef, SimpleSnackBar } from '@angular/material/snack-bar';

export interface TransportPlannerProfileItemBriefStateModel extends TransportPlannerProfileDetails {
  loaded: boolean;
}

const initialState: TransportPlannerProfileItemBriefStateModel = {
  ...new TransportPlannerProfileDetails(),
  loaded: false,
};

@State<TransportPlannerProfileItemBriefStateModel>({
  name: 'transportPlannerProfileItemBrief',
  defaults: initialState,
})
@Injectable()
export class TransportPlannerProfileItemBriefState {
  constructor(
    private transportPlannerProfileItemBriefService: TransportPlannerProfileItemBriefService,
    private transportPlannerProfileItemService: TransportPlannerProfileItemService,
    private notificationService: NotificationService,
    private navigateUserService: NavigateUserService,
  ) {}

  @Selector()
  static getProfileId(state: TransportPlannerProfileItemBriefStateModel): number {
    return state.id;
  }

  @Selector()
  static getState(state: TransportPlannerProfileItemBriefStateModel): TransportPlannerProfileItemBriefStateModel {
    return state;
  }

  @Selector()
  static getLoaded(state: TransportPlannerProfileItemBriefStateModel): boolean {
    return state.loaded;
  }

  @Action(TransportPlannerProfileItemBriefLoad)
  getTransportPlannerProfileItemBrief(
    ctx: StateContext<TransportPlannerProfileItemBriefStateModel>,
    { payload }: TransportPlannerProfileItemBriefLoad,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.transportPlannerProfileItemService.getTransportPlannerProfile(payload).pipe(
      map((transportPlannerProfile: TransportPlannerProfileModel) => {
        ctx.patchState({ ...transportPlannerProfile, loaded: true });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(TransportPlannerProfileItemBriefSave)
  saveTransportPlannerProfileItemBrief(
    ctx: StateContext<TransportPlannerProfileItemBriefStateModel>,
    { payload }: TransportPlannerProfileItemBriefSave,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.transportPlannerProfileItemBriefService.saveTransportPlannerProfile(payload).pipe(
      map((updatedProfile: TransportPlannerProfileModel) => {
        ctx.patchState({ ...updatedProfile });
        this.notificationService.success('api.transportPlannerProfile.success.update');
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(TransportPlannerProfileItemBriefClone)
  cloneTransportPlannerProfileItemBrief(
    ctx: StateContext<TransportPlannerProfileItemBriefStateModel>,
    { payload }: TransportPlannerProfileItemBriefClone,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.transportPlannerProfileItemBriefService.cloneTransportPlannerProfile(payload).pipe(
      map(() => {
        this.notificationService.success('api.transportPlannerProfile.success.create');
        this.navigateUserService.navigateUserToAbsolutePath('/dashboard/monitoring/transport-planner-profiles');
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(TransportPlannerProfileItemBriefDelete)
  deleteTransportPlannerProfileItemBrief(
    ctx: StateContext<TransportPlannerProfileItemBriefStateModel>,
    { payload }: TransportPlannerProfileItemBriefDelete,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | Observable<void>> {
    return this.transportPlannerProfileItemBriefService.deleteTransportPlannerProfile(payload.profileId).pipe(
      map(() => {
        payload.dialog.close();
        this.notificationService.success('api.transportPlannerProfile.success.delete');
        this.navigateUserService.navigateUserToAbsolutePath('/dashboard/monitoring/transport-planner-profiles');
        return ctx.dispatch(new TransportPlannerProfileItemBriefInitialState());
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(TransportPlannerProfileItemBriefInitialState)
  setInitialState(ctx: StateContext<TransportPlannerProfileItemBriefStateModel>): void {
    ctx.setState(initialState);
  }
}
